import React, { useState } from 'react';
import { errorMessage, getStatisticsTimeline } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import type { StatisticsTimeline, TimelinePoint } from '../../types';
import { Card, CardBody, InlineAlert, SelectField, Skeleton, Table, Td, Th } from '../../ui';
import { num } from './metrics';

type Interval = 'day' | 'week' | 'month';

/** Outcomes drawn in each bar, bottom to top. Entries are in the table: they include people still in the survey. */
const SEGMENTS: { key: 'complete' | 'screenout' | 'quotaFull' | 'timedOut' | 'qualityTerminate'; label: string; color: string }[] = [
  { key: 'complete', label: 'Complete', color: 'bg-ok' },
  { key: 'screenout', label: 'Screen-out', color: 'bg-slate-400' },
  { key: 'quotaFull', label: 'Quota full', color: 'bg-warn' },
  { key: 'timedOut', label: 'Timed out', color: 'bg-orange-300' },
  { key: 'qualityTerminate', label: 'Quality terminate', color: 'bg-danger' },
];

const outcomes = (p: TimelinePoint) => SEGMENTS.reduce((sum, s) => sum + (p[s.key] || 0), 0);

interface TrendCardProps {
  projectId: string;
  groups: { id: string; name: string }[];
  /** the days respondents entered (YYYY-MM-DD, both inclusive); empty = no limit */
  from: string;
  to: string;
  /** the time zone the days are cut in */
  tz: string;
}

/**
 * The campaign's figures over time (GET /project/{id}/statistics/timeline): one stacked bar per day, week (starting Monday) or month, with
 * the same figures in a table below. `entries` is counted on the day a respondent entered; a final status on the day it was got (a respondent
 * who was reconciled counts under the current status). Quiet days are zeros between the first and last activity. A campaign with no respondent
 * sessions only has complete, screen-out and quota full. Bars are scaled to the busiest period; nothing is smoothed or estimated.
 */
export function TrendCard({ projectId, groups, from, to, tz }: TrendCardProps) {
  const [interval, setInterval] = useState<Interval>('day');
  const [targetGroupId, setTargetGroupId] = useState('');
  const timeline = useAsyncData<StatisticsTimeline | null>(
    (signal) => getStatisticsTimeline(projectId, { interval, from, to, tz, targetGroupId }, { signal }),
    [projectId, interval, from, to, tz, targetGroupId]
  );
  const t = timeline.data;
  const points: TimelinePoint[] = t ? t.points : [];
  const sessions = Boolean(t && t.source === 'sessions');
  const peak = points.reduce((max, p) => Math.max(max, outcomes(p)), 0);
  const shown = sessions ? SEGMENTS : SEGMENTS.slice(0, 3);
  const shownInterval = (t && t.interval) || interval; // labels follow the data on screen until the new interval has loaded

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink">Over time</h2>
            <p className="mt-1 text-sm text-muted">Outcomes per {interval}, counted on the day each one happened.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <SelectField label="Per" value={interval} onChange={(e) => setInterval(e.target.value as Interval)}>
              <option value="day">Day</option>
              <option value="week">Week (from Monday)</option>
              <option value="month">Month</option>
            </SelectField>
            <SelectField label="Target group" value={targetGroupId} onChange={(e) => setTargetGroupId(e.target.value)} disabled={t !== null && !sessions} hint={t !== null && !sessions ? 'Respondent tracking only' : undefined}>
              <option value="">All target groups</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </SelectField>
          </div>
        </div>

        {timeline.error ? (
          <div className="mt-4"><InlineAlert tone="warn">Could not load the trend: {errorMessage(timeline.error)} <button type="button" onClick={timeline.reload} className="font-semibold underline">Try again</button></InlineAlert></div>
        ) : !timeline.data && timeline.loading ? (
          <div className="mt-4" aria-busy="true"><Skeleton className="h-44 w-full" /></div>
        ) : !t ? (
          <div className="mt-4"><InlineAlert tone="info">No trend is available for this campaign.</InlineAlert></div>
        ) : points.length === 0 || peak === 0 ? (
          <div className="mt-4"><InlineAlert tone="info">No activity {from || to ? 'in this period' : 'yet'}.</InlineAlert></div>
        ) : (
          <>
            {!sessions && <p className="mt-3 text-xs text-muted">Based on the survey links only: complete, screen-out and quota full. Timed-out and in-survey figures need respondent sessions.</p>}
            <div className="mt-4 overflow-x-auto">
              <div className="flex h-44 min-w-full items-end gap-1" role="img" aria-label={`Stacked bars of outcomes per ${shownInterval}, from ${points[0].period} to ${points[points.length - 1].period}. The table below has the same figures.`}>
                {points.map((p) => (
                  <div key={p.period} className="flex h-full min-w-[14px] flex-1 flex-col-reverse" title={`${p.period}: ${shown.map((s) => `${s.label} ${p[s.key] || 0}`).join(', ')}`}>
                    {shown.map((s) => {
                      const value = p[s.key] || 0;
                      return value > 0 ? <span key={s.key} className={s.color} style={{ height: `${(value / peak) * 100}%` }} /> : null;
                    })}
                  </div>
                ))}
              </div>
              <div className="mt-1 flex justify-between text-[11px] text-muted"><span>{points[0].period}</span><span>{points[points.length - 1].period}</span></div>
            </div>
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted" aria-label="Chart legend">
              {shown.map((s) => <li key={s.key} className="flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-sm ${s.color}`} aria-hidden="true" />{s.label}</li>)}
            </ul>
            <details className="mt-4">
              <summary className="cursor-pointer text-sm font-semibold text-brand-700">Show the figures as a table</summary>
              <div className="-mx-5 mt-2">
                <Table caption={`Outcomes per ${shownInterval}`}>
                  <thead><tr><Th>{shownInterval === 'day' ? 'Day' : shownInterval === 'week' ? 'Week from' : 'Month from'}</Th>{sessions && <Th numeric>Entries</Th>}<Th numeric>Complete</Th><Th numeric>Screen-out</Th><Th numeric>Quota full</Th>{sessions && <Th numeric>Timed out</Th>}{sessions && <Th numeric>Quality terminate</Th>}</tr></thead>
                  <tbody>
                    {points.map((p) => (
                      <tr key={p.period} className="hover:bg-canvas">
                        <Td className="tabular-nums">{p.period}</Td>
                        {sessions && <Td numeric className="tabular-nums text-muted">{num(p.entries)}</Td>}
                        <Td numeric className="tabular-nums">{num(p.complete)}</Td>
                        <Td numeric className="tabular-nums text-muted">{num(p.screenout)}</Td>
                        <Td numeric className="tabular-nums text-muted">{num(p.quotaFull)}</Td>
                        {sessions && <Td numeric className="tabular-nums text-muted">{num(p.timedOut)}</Td>}
                        {sessions && <Td numeric className="tabular-nums text-muted">{num(p.qualityTerminate)}</Td>}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </details>
          </>
        )}
      </CardBody>
    </Card>
  );
}
